// Launchpad page copy. Step content is keyed by the step ids in setupSteps.js.
//
// Step content:
//   title / hint / why   row copy; `why` renders behind a "Why?" toggle
//   lockedHint           copy while a prerequisite step is incomplete (the CTA is disabled)
//   upgradeHint          copy while the step's product is locked
//   tagScope             scope shown on the tag when it differs from the step's scope
//   icon                 CTA icon key (see SetupTaskRow)
//   cta                  { label, route } | { label, href } | { label, modal } | { label, tour } | { label, flow }
//                        modal: 'invite' | 'instagram' | 'mcp' (owned by SetupView)
//                        tour: a TOURS id in tourContent.js
//                        flow: a guided flow owned by SetupView ('save-ad' | 'board-share' | 'spyder-brand' | 'ad-rank' |
//                        'lens-top-performing' | 'lens-comparison')

import { MCP_NOT_CONNECTED_LINE } from './mcpSkills'

export const SETUP_HEADER = {
  kicker: 'Launchpad',
  title: "Welcome to Foreplay{name}. Let's get you set up.",
  subhead: 'Every step switches something on: competitors tracked, your ad account decoded, your AI plugged into all of it. Any order, two minutes each.',
  trialLine: 'Trial · {days} days left · every feature unlocked',
  completeLine: 'Launched · every step complete',
  progressLabel: 'Launchpad Completion',
  progressLabelComplete: 'Launched'
}

// Product strip in each section header; keys map to the sidebar's animated product sprites ('mcp' is a static icon)
export const SETUP_TOOLS_TITLE = 'Foreplay tools'

// Sticky jump-to-section nav under the account section
export const SETUP_SECTION_NAV_TITLE = '3 Core Areas of Foreplay'
export const SETUP_SECTION_NAV_TITLE_COMPACT = '3 Core Areas'

export const SETUP_SCOPE_TAGS = {
  workspace: {
    label: 'Account Wide',
    tooltip: 'Done once for the whole workspace'
  },
  user: {
    label: 'Each Team Member',
    tooltip: 'Every teammate completes this themselves'
  }
}

export const SETUP_PRODUCT_LOCKED = {
  hint: "This isn't included in your current plan. Upgrade to unlock it.",
  badgeLabel: 'Upgrade',
  ctaLabel: 'Upgrade'
}

// Subdued skip control under each row's CTA
export const SETUP_SKIP = {
  skipLabel: 'Skip',
  undoLabel: 'Undo skip',
  skippedLabel: 'Skipped',
  errorMessage: "Couldn't update that step. Please try again."
}

export const SETUP_PHONE = {
  title: 'Foreplay is built for a bigger screen.',
  body: 'Below is your onboarding checklist to get set up with Foreplay. Feel free to look around and watch the videos.',
  // Bold, on its own line under body
  bodyEmphasis: 'Complete your onboarding on desktop or tablet.',
  copyLabel: 'Copy Link & Open on Desktop',
  copiedLabel: 'Link Copied',
  copyFallbackMessage: 'Open this link on your computer: {url}',
  stickyLabel: 'Copy the link and open on your computer to complete the onboarding Launchpad',
  pillLabel: 'Finish on desktop'
}

// Copy for the curated brand picker (SetupBrandPickerModal). The brand list
// lives in curatedBrands.js.
export const SETUP_BRAND_PICKER = {
  kicker: 'Guided walkthrough · Step 1 of 3',
  title: 'Pick a brand to track',
  blurb: 'Foreplay tracks over 500K brands 24/7 — start by tracking one of the most popular brands and you can add your own competitors after.',
  // {count} and {category} are filled in from the selected tab
  gridCaption: 'Top {count} in {category}',
  gridCaptionMore: 'thousands more in each',
  moreBrandsNote: 'Not seeing a brand you want? Every one of the 500K+ brands is searchable in Spyder, and you can add any brand manually from its Ad Library URL.',
  outOfCreditsNote: 'untrack a brand first',
  ctaLabel: 'Track Brand',
  ctaLabelLoading: 'Tracking…'
}

// One-click names offered in the create-board modal during the guided flow
export const BOARD_NAME_SUGGESTIONS = [
  'Q4 Hooks',
  'UGC References',
  'Competitor Winners',
  'Landing Page Inspo',
  'Static Ads'
]

// Middle step of the board flow (SetupBoardSeedModal): previews the ads about
// to be added to the new board. {source} is the tracked brand's name, or
// "Discovery" when there isn't one.
// One-click board names in the Instagram connect modal (after a "<First name>'s Saves" chip)
export const IG_BOARD_SUGGESTIONS = [
  'Mobile Saves',
  'Phone Saves',
  'Reels Inspo',
  'Scroll Stoppers',
  'UGC References'
]

export const SETUP_BOARD_SEED = {
  kicker: 'Guided walkthrough · Step 2 of 3',
  title: "We'll add these to your board",
  blurb: "A few ads from {source}, so there's something worth sharing. You can remove any of them later.",
  emptyNote: "We couldn't find ads to add right now. Your board is still ready to share.",
  ctaLabel: 'Add These Ads',
  ctaLabelEmpty: 'Continue'
}

export const MCP_CLIENTS = [
  { id: 'claude', label: 'Claude' },
  { id: 'chatgpt', label: 'ChatGPT' }
]

// Shown above the prompts until mcp_connected is done
export const MCP_PROMPTS_NOT_CONNECTED_NOTE = "Connect the MCP first, or your AI won't have Foreplay's data to answer with."

// One-off first wins for Setup MCP. Discovery-only on purpose: they work the
// moment the MCP is connected, with nothing tracked, saved or synced. Keep them
// distinct from MCP_SKILLS (mcpSkills.js) — skills are the repeatable
// workflows, so a prompt that matches a skill belongs in the skill instead.
export const MCP_PROMPTS = [
  {
    id: 'brand-snapshot',
    title: 'Brand Snapshot',
    oneLiner: 'What any brand is running right now: newest ads, formats and offers.',
    prompt: `Using the Foreplay MCP, look up [brand] and show me 6 of their newest live ads with display_ad_results. Then summarize in 3 bullets: the formats they're leaning on, the offers they're pushing, and the one angle that shows up most. ${MCP_NOT_CONNECTED_LINE}`,
    wow: 'A read on any brand in seconds, no Ad Library scrolling.',
    tools: ['brand search', 'brand ads', 'display_ad_results'],
    needs: ['Discovery']
  },
  {
    id: 'ugc-script',
    title: 'Steal a UGC Script',
    oneLiner: 'A live UGC ad in your category, rewritten for your product.',
    prompt: `Using the Foreplay MCP, search Discovery for live UGC video ads for [product type] and pick the one that has run longest. Show it with display_ad_results, pull its transcript, and rewrite it as a 30-second script for my product: [your product]. Keep the structure, change the words. ${MCP_NOT_CONNECTED_LINE}`,
    wow: 'A proven structure turned into a script a creator can shoot this week.',
    tools: ['discovery search', 'transcripts', 'display_ad_results'],
    needs: ['Discovery']
  },
  {
    id: 'offer-scan',
    title: 'Offer Scan',
    oneLiner: 'Which discounts, bundles and guarantees your niche actually runs.',
    prompt: `Using the Foreplay MCP, search Discovery for live ads in [niche] and list every offer you can find: discounts, bundles, free shipping, guarantees, trials. Rank them by how often they appear, and show one ad for each of the top 3 with display_ad_results. ${MCP_NOT_CONNECTED_LINE}`,
    wow: 'See the offer your market expects before you price your next test.',
    tools: ['discovery search', 'display_ad_results'],
    needs: ['Discovery']
  },
  {
    id: 'format-mix',
    title: 'Format Mix Check',
    oneLiner: 'Video vs static vs carousel across live ads in your niche.',
    prompt: `Using the Foreplay MCP, search Discovery for live ads in [niche] and break down the mix of video, static image and carousel ads. Show the strongest example of each format with display_ad_results and tell me which format looks under-used. ${MCP_NOT_CONNECTED_LINE}`,
    wow: 'Spot the format gap in your niche before you brief the next batch.',
    tools: ['discovery search', 'display_ad_results'],
    needs: ['Discovery']
  },
  {
    id: 'ad-copy-swipe',
    title: 'Ad Copy Swipe',
    oneLiner: 'Ten strong static ads in your niche, copy rewritten for you.',
    prompt: `Using the Foreplay MCP, find 10 long-running static image ads in [niche] and show them with display_ad_results. Pull their primary text and headlines, then rewrite the best 5 for my product: [your product]. ${MCP_NOT_CONNECTED_LINE}`,
    wow: 'Copy that has already survived real spend, ready to adapt.',
    tools: ['discovery search', 'display_ad_results'],
    needs: ['Discovery']
  }
]

// Sections render in this order. `navLabel` puts a section in the sticky nav;
// `tools` fills its product strip; `learn` adds the video column.
// Videos: { provider: 'youtube', youtubeId, start?, duration?, title, gradient }
export const SETUP_SECTIONS = [
  {
    id: 'account',
    accent: 'blue',
    kicker: 'Pre-flight',
    title: "You're one step closer to your best performing ad of all time.",
    blurb: "You have the first step done, let's keep this momentum going. Add a team member and then start getting inspired, analyzing competitors and optimizing your own ads."
  },
  {
    id: 'research',
    accent: 'blue',
    kicker: "Find what's working",
    navLabel: 'Competitor Ads & Inspiration',
    tools: [
      { key: 'spyder', name: 'Spyder', descriptor: 'Track', blurb: 'Watches the brands you pick and flags every new ad, test and hook.', route: { name: 'SpyderBrandsView' } },
      { key: 'library', name: 'Swipe File', descriptor: 'Save & Share', blurb: 'Your permanent library of saved ads — video, copy, landing page and transcript included.', route: { name: 'LibraryView' } },
      { key: 'discovery', name: 'Discovery', descriptor: 'Search', blurb: 'Search 200M+ live ads by brand, keyword or niche.', route: { name: 'DiscoveryView' } }
    ],
    title: 'Know what every competitor is running before your next brief.',
    blurb: 'Three tools, one job. Spyder watches the brands you pick and tells you when they launch something new. Swipe File saves any ad forever. Discovery searches 200M+ ads.',
    learn: {
      video: { provider: 'youtube', youtubeId: '5ujXBm_kD-Q', start: 1, title: 'Save Ads from Meta, TikTok, LinkedIn Ad Libraries in One Click', gradient: 'blue' }
    }
  },
  {
    id: 'lens',
    accent: 'blue',
    kicker: "Know what's working for you",
    navLabel: 'Analyze My Own Ad Performance',
    tools: [
      { key: 'lens', name: 'Lens', descriptor: 'Reporting', blurb: 'Reads your ad accounts and reports by hook, format and theme — not campaign ID.', route: { name: 'LensView' } },
      { key: 'briefs', name: 'Briefs', descriptor: 'Production', blurb: 'Turn a winning pattern into a brief your creators can run with.', route: { name: 'BriefsView' } }
    ],
    title: "Know what's working in your own account, and why.",
    blurb: 'Lens connects to your ad accounts and reads creative the way a strategist does: by hook, format and theme, not campaign ID. Read-only, and your data never touches the public index.',
    learn: {
      video: { provider: 'youtube', youtubeId: '93_VRP1c_a4', start: 10, title: 'Introducing Lens Creative Analytics', gradient: 'green' },
      secondaryVideos: [
        { provider: 'youtube', youtubeId: '-1urtxQXG84', start: 0, title: 'Multi-Account Reporting in Lens', gradient: 'green' }
      ]
    }
  },
  {
    id: 'mcp',
    accent: 'blue',
    kicker: 'Put your AI to work',
    navLabel: 'Connect to Your AI',
    tools: [
      { key: 'mcp', name: 'Foreplay MCP', blurb: 'Gives Claude or ChatGPT the same 200M+ ads, your boards and your Lens data.', route: { name: 'ApiView' } }
    ],
    title: "Ask Claude or ChatGPT about any brand's ads. Foreplay gives it the answers.",
    blurb: 'The MCP connects your AI to the same 200M+ ads, your boards and your Lens data. Unlock free API credits if needed, connect your client, then run a wow prompt or install a skill pack.',
    learn: {
      video: { provider: 'youtube', youtubeId: 'fbr_WGlSslA', start: 32, duration: '11:26', title: 'This New MCP Gives Claude Access to The Meta Ad Library', gradient: 'yellow' },
      secondaryVideos: [
        { provider: 'youtube', youtubeId: 'TqzUKpgjbwg', start: 0, title: 'Access the Entire Database with the API', gradient: 'blue' }
      ]
    }
  }
]

export const SETUP_STEP_CONTENT = {
  email_verified: {
    title: 'Verify Your Email',
    hint: 'Done at signup. One down already.'
  },
  extension_installed: {
    title: 'Install the Chrome Extension',
    hint: 'Save ads from any ad library, Instagram organic and more.',
    why: 'The ad you screenshotted last week is gone from the ad library already. The extension saves the video, the copy, the landing page and a transcript, permanently.',
    icon: 'chrome',
    cta: { label: 'Add to Chrome', href: 'https://chrome.google.com/webstore/detail/ad-library-save-facebook/eaancnanphggbfliooildilcnjocggjm' }
  },
  teammate_invited: {
    title: 'Invite a Teammate',
    hint: 'Foreplay is better with friends. Collaborate in a shared workspace.',
    why: "Great ads come from teams working off the same page. When creative and performance share one swipe file, one set of boards and one read on what's working, every brief starts from the same evidence, and the ads get better because of it.",
    icon: 'inviteUser',
    cta: { label: 'Send Invite', modal: 'invite' }
  },
  spyder_track: {
    title: 'Track a Brand in Spyder',
    hint: 'Start with a suggested brand we know is crushing paid social.',
    upgradeHint: 'Spyder is not on your plan. Upgrade to track brands.',
    why: 'More than simply tracking every ad your competitors launch 24/7, Spyder analyzes that data to reveal the ads with the most impressions, their creative tests, and extracts all their hooks.',
    icon: 'spyder',
    cta: { label: 'Track a Brand', flow: 'spyder-brand' }
  },
  ad_rank_opened: {
    title: "Reveal a Brand's Top Ads",
    hint: 'Open the Ad Rank tab in Spyder to see the ads getting the most impressions.',
    upgradeHint: "Ad Rank is part of Spyder, which is not on your plan. Upgrade to rank a brand's ads.",
    lockedHint: 'Ad Rank works on a tracked brand. Track one first and this unlocks.',
    why: "Ad Rank sorts a tracked brand's library by impressions and scaling velocity, so you see the two or three ads doing the work — not the two hundred that aren't.",
    icon: 'adRank',
    cta: { label: 'Open Ad Rank', flow: 'ad-rank' }
  },
  first_ad_saved: {
    title: 'Save Your First Ad',
    hint: 'Use the save button to save an ad to your board or swipe file.',
    why: 'Saving is the habit everything else builds on. Saved ads get transcribed and tagged automatically, so a month from now you can search your own swipe file.',
    icon: 'save',
    cta: { label: 'Save an Ad', flow: 'save-ad' }
  },
  board_shared: {
    title: 'Create & Share a Board',
    hint: 'Shareable inspiration boards to organize ads (e.g. "Q4 Hooks", "UGC References").',
    lockedHint: "We fill your first board with a tracked brand's ads. Track one first and this unlocks.",
    why: "Sharing is where Foreplay starts saving other people's time too. Viewers don't need an account, and the board updates as you add to it.",
    icon: 'newBoard',
    cta: { label: 'Create Board', flow: 'board-share' }
  },
  discovery_search: {
    title: 'Search a Keyword in Discovery',
    hint: 'Discover brands and products you never knew existed across 200M+ ads and 500K companies.',
    why: 'Great inspiration goes beyond the companies you already know about. Discover hidden brands and products adjacent to your niche.',
    icon: 'search',
    cta: { label: 'Search Ads', tour: 'discovery-search' }
  },
  instagram_connected: {
    title: 'Connect Instagram for Mobile Saving',
    hint: 'Save ads from your phone simply by DMing content to @foreplay_co.',
    why: 'Half the ads worth stealing show up in your own feed. Connect Instagram once and saving becomes a DM — no screenshots, no lost links.',
    icon: 'instagram',
    cta: { label: 'Connect IG', modal: 'instagram' }
  },
  lens_connect: {
    title: 'Connect Your Own Ad Account',
    hint: 'Meta or TikTok, your first data sync will take 24–48 hrs.',
    upgradeHint: 'Lens is not on your plan. Upgrade to connect an ad account.',
    why: "Lens groups every creative you've run by ad name, copy or format and shows which ones carry the account. Nobody else can see this data; it's isolated to your workspace.",
    icon: 'meta',
    cta: { label: 'Add Account', tour: 'lens-connect' }
  },
  lens_top_performing: {
    title: 'Read a Top Performing Report',
    hint: 'See when and how to use Top Performing reports with sample data.',
    why: 'Group by ad name, copy or creative to find and share winning patterns. The demo uses Foreplay ads so you can learn the report before your data sync is complete.',
    icon: 'topPerforming',
    cta: { label: 'View Report', flow: 'lens-top-performing' }
  },
  lens_comparison: {
    title: 'Read a Comparison Report',
    hint: 'Learn how to group and segment your ad reports to compare unique groups of ads.',
    why: 'Use filters to segment your own unique groups of ads to compare things like creators, vendors, formats, etc. This demo compares agency, external creators, and in-house by ad name.',
    icon: 'comparison',
    cta: { label: 'View Report', flow: 'lens-comparison' }
  },
  mcp_connected: {
    title: 'Connect the MCP',
    hint: "We'll unlock the API if needed, then walk you through your client. OAuth checks this off.",
    why: "Your AI is only as good as what it can see. Connected, it can search the index, read your boards, pull Spyder tracking and Lens metrics, and do the Monday competitor sweep while you're asleep.",
    tagScope: 'user',
    icon: 'mcp',
    cta: { label: 'Connect MCP', modal: 'mcp' }
  },
  mcp_prompt: {
    title: 'Run Your First Wow Prompt',
    hint: 'Copy a prompt below and paste it into your AI. We detect the first tool call.',
    why: "The first prompt is the moment the MCP goes from configured to useful. Run one and you'll know exactly what to ask next.",
    tagScope: 'user'
  }
}
